import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "./hooks/useAuth";
import api, { setAuthToken } from "./services/api";
import TrialNotificationModal, {
  TrialFloatingButton,
} from "./components/TrialNotificationModal";
import { TrialProvider, useTrial } from "./contexts/TrialContext";
import FeedbackModal from "./components/FeedbackModal";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RunNotifierProvider } from "./contexts/RunNotifierContext";
import "./styles/App.css";
import "./styles/scoreDashboard.css";
import "./styles/frontend-style.css";
import { AppRoutes } from "./AppRoutes";

// ─────────────────────────────────────────────────────────────
// Main App Content (inside BrowserRouter)
// ─────────────────────────────────────────────────────────────
function AppContent({
  auth,
  agents,
  loading,
  projects,
  quota,
  projectsError,
  onRetryProjects,
  onProjectsChanged,
}) {
  const navigate = useNavigate();
  const isAdminOrSubadmin =
    auth.user?.role === "admin" || auth.user?.role === "subadmin";

  const trial = useTrial();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Project gate — the app needs one project with a main domain before any
  // agent can run. Staff keep access to /admin so they cannot lock themselves
  // out, and the terms modal takes precedence over everything.
  const incompleteProject = projects?.find((p) => !p.isComplete) || null;
  // Internal staff with free URL entry are not pushed through project
  // onboarding — they can run agents without a project at all. They may still
  // create projects whenever they want; they are simply never forced to.
  const unrestrictedTargets = !!auth.user?.unrestrictedTargets;

  // const needsOnboarding =
  //   auth.termsAccepted &&
  //   !isAdminPath &&
  //   !unrestrictedTargets &&
  //   (projects?.length === 0 || !!incompleteProject);

  // const RedirectToTerms = () => {
  //   window.location.replace("/saffronos-terms.html");
  //   return null;
  // };
  // const RedirectToPrivacy = () => {
  //   window.location.replace("/privacy-policy.html");
  //   return null;
  // };

  // return (
  //   <>
  //     {!auth.termsAccepted && (
  //       <TermsAcceptanceModal
  //         onAccept={onAcceptTerms}
  //         onDecline={auth.logout}
  //         loading={termsLoading}
  //       />
  //     )}

  return (
    <>
      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />

      {/* Kept mounted while a trial scenario is active so the form/submitted
          state survives closing & re-opening via the floating button. */}
      {auth.termsAccepted && trial?.scenario && (
        <TrialNotificationModal
          open={trial.open}
          scenario={trial.scenario}
          onDismiss={trial.dismiss}
          submitted={trial.submitted}
          onSubmitted={trial.markSubmitted}
          onRatingSaved={trial.markRating}
        />
      )}

      {auth.termsAccepted && trial?.scenario && !trial.open && (
        <TrialFloatingButton
          scenario={trial.scenario}
          onClick={trial.openModal}
        />
      )}

      <AppRoutes
        auth={auth}
        agents={agents}
        loading={loading}
        projects={projects}
        quota={quota}
        projectsError={projectsError}
        onRetryProjects={onRetryProjects}
        onProjectsChanged={onProjectsChanged}
        isAdminOrSubadmin={isAdminOrSubadmin}
        onOpenFeedback={() => setFeedbackOpen(true)}
        navigate={navigate}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────

function App() {
  const auth = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  // Project gate — the app is unusable until the user owns one project that
  // has a main domain. `null` means "not checked yet".
  const [projects, setProjects] = useState(null);
  // Project allowance that came with the list. The header's project dropdown
  // shows it ("New Project (2/3)"), which the Agent Library used to do with
  // its own copy of this request.
  const [quota, setQuota] = useState(null);
  const [projectsError, setProjectsError] = useState(false);

  useEffect(() => {
    if (auth.token) {
      setAuthToken(auth.token);
      loadAgents();
      // Guests have no projects and the project API is closed to them, so
      // asking would only produce a 403 and a spinner that never resolves.
      if (auth.user?.role === "guest") {
        setProjects([]);
        setQuota(null);
      } else loadProjects();
    } else {
      setProjects(null);
      setQuota(null);
      setProjectsError(false);
      setAgents([]);
    }
  }, [auth.token]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getAgents();
      setAgents(response.data.agents);
    } catch (err) {
      console.error("Error loading agents:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    setProjectsError(false);
    try {
      const response = await api.getProjects();
      setProjects(response.data.projects || []);
      setQuota(response.data.quota || null);
    } catch (err) {
      console.error("Error loading projects:", err);
      setProjectsError(true);
      //setProjects([]);
    }
  };

  return (
    <RunNotifierProvider>
      <TrialProvider user={auth.user}>
        <AppContent
          auth={auth}
          agents={agents}
          loading={loading}
          projects={projects}
          quota={quota}
          projectsError={projectsError}
          onRetryProjects={loadProjects}
          onProjectsChanged={loadProjects}
        />
        <ToastContainer
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </TrialProvider>
    </RunNotifierProvider>
  );
}

export default App;
